// Shared canvas drawing helpers — gold tournament table, decorative corner
// ornaments, and rounded paddles. Used by both PongGame (host) and Controller
// (mirror) so visuals stay in lockstep.

export const W = 1000;
export const H = 600;

// Black table for "Bong" with a soft purple-haze wash near the margins —
// matches the page's purple-neon aesthetic.  Centre stays deep black.
export function drawArenaBackground(ctx) {
  // 1. Solid black base.
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, W, H);

  // 2. Purple-haze radial wash from the edges inward.  We paint four
  //    soft radial gradients in the corners + one ring around the
  //    midline so the centre stays inky while the margins glow.
  const haze = (cx, cy, r0, r1, inner, outer) => {
    const g = ctx.createRadialGradient(cx, cy, r0, cx, cy, r1);
    g.addColorStop(0, inner);
    g.addColorStop(1, outer);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  };
  haze(0,       0,       0,   W * 0.7, "rgba(192,132,252,0.32)", "rgba(192,132,252,0)");
  haze(W,       0,       0,   W * 0.7, "rgba(192,132,252,0.32)", "rgba(192,132,252,0)");
  haze(0,       H,       0,   W * 0.7, "rgba(192,132,252,0.32)", "rgba(192,132,252,0)");
  haze(W,       H,       0,   W * 0.7, "rgba(192,132,252,0.32)", "rgba(192,132,252,0)");

  // 3. Inner border — purple neon rule line.
  ctx.save();
  ctx.strokeStyle = "rgba(192,132,252,0.7)";
  ctx.lineWidth = 2;
  ctx.shadowColor = "#C084FC";
  ctx.shadowBlur = 14;
  ctx.strokeRect(10, 10, W - 20, H - 20);
  ctx.shadowBlur = 0;
  ctx.lineWidth = 1;
  ctx.strokeStyle = "rgba(192,132,252,0.35)";
  ctx.strokeRect(16, 16, W - 32, H - 32);
  ctx.restore();

  // 4. Centre divider — dashed purple midline.
  ctx.save();
  ctx.strokeStyle = "rgba(192,132,252,0.55)";
  ctx.shadowColor = "#C084FC";
  ctx.shadowBlur = 8;
  ctx.lineWidth = 3;
  ctx.setLineDash([14, 16]);
  ctx.beginPath();
  ctx.moveTo(W / 2, 22);
  ctx.lineTo(W / 2, H - 22);
  ctx.stroke();
  ctx.restore();
}

function drawCornerFlourish(ctx, cx, cy, rotation) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);
  ctx.strokeStyle = "rgba(58, 32, 12, 0.7)";
  ctx.fillStyle = "rgba(58, 32, 12, 0.55)";
  ctx.lineWidth = 1.6;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Fleur-de-lis: three lobes with a horizontal band.
  // Centre lobe (vertical leaf)
  ctx.beginPath();
  ctx.moveTo(0, -16);
  ctx.bezierCurveTo(-3, -12, -3, -4, 0, -1);
  ctx.bezierCurveTo(3, -4, 3, -12, 0, -16);
  ctx.fill();
  ctx.stroke();
  // Side curls
  ctx.beginPath();
  ctx.moveTo(0, -2);
  ctx.bezierCurveTo(-10, -6, -14, 2, -8, 6);
  ctx.bezierCurveTo(-12, 2, -8, -2, -2, 0);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0, -2);
  ctx.bezierCurveTo(10, -6, 14, 2, 8, 6);
  ctx.bezierCurveTo(12, 2, 8, -2, 2, 0);
  ctx.stroke();
  // Horizontal band
  ctx.beginPath();
  ctx.moveTo(-12, 1);
  ctx.lineTo(12, 1);
  ctx.lineWidth = 2.2;
  ctx.stroke();
  // Tail
  ctx.beginPath();
  ctx.moveTo(0, 2);
  ctx.lineTo(0, 12);
  ctx.lineWidth = 1.6;
  ctx.stroke();
  ctx.restore();
}

function drawCentreMedallion(ctx, cx, cy) {
  ctx.save();
  ctx.globalAlpha = 0.35;
  ctx.strokeStyle = "#5A3D14";
  ctx.fillStyle = "rgba(90, 61, 20, 0.18)";
  ctx.lineWidth = 1.5;
  // outer ring
  ctx.beginPath();
  ctx.arc(cx, cy, 60, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, 50, 0, Math.PI * 2);
  ctx.stroke();
  // sun rays
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * 30, cy + Math.sin(a) * 30);
    ctx.lineTo(cx + Math.cos(a) * 48, cy + Math.sin(a) * 48);
    ctx.stroke();
  }
  // inner disc
  ctx.beginPath();
  ctx.arc(cx, cy, 26, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

// Color blending helpers for the liquid-vial paddles.
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbMix(hex, target, t) {
  const [r, g, b] = hexToRgb(hex);
  const R = Math.round(r + (target - r) * t);
  const G = Math.round(g + (target - g) * t);
  const B = Math.round(b + (target - b) * t);
  return `rgb(${R},${G},${B})`;
}
const lighten = (hex, t) => rgbMix(hex, 255, t);
const darken = (hex, t) => rgbMix(hex, 0, t);

// Rounded liquid-vial paddle — glowing outer halo, dark glass rim, bright
// inner gradient and a specular highlight stripe. Optional silver aegis aura.
export function drawPaddle(ctx, x, y, w, h, color, aegis) {
  const r = 6;

  if (aegis) {
    ctx.save();
    ctx.shadowColor = "#E8EEF8";
    ctx.shadowBlur = 28;
    ctx.fillStyle = "rgba(232,238,248,0.22)";
    roundRectPath(ctx, x - 6, y - 6, w + 12, h + 12, r + 4);
    ctx.fill();
    ctx.restore();
  }

  // 1. Outer glow — soft colored halo radiating from the paddle.
  ctx.save();
  ctx.shadowColor = color;
  ctx.shadowBlur = 38;
  ctx.fillStyle = color;
  roundRectPath(ctx, x, y, w, h, r);
  ctx.fill();
  // Second pass to deepen the glow.
  ctx.shadowBlur = 22;
  ctx.fill();
  // Third pass for a tight bright core.
  ctx.shadowBlur = 10;
  ctx.fill();
  ctx.restore();

  // 2. Liquid interior — vertical gradient with a brighter core to look like
  //    backlit fluid behind glass.
  const darkEdge = darken(color, 0.55);
  const brightCore = lighten(color, 0.55);
  ctx.save();
  const grad = ctx.createLinearGradient(x, 0, x + w, 0);
  grad.addColorStop(0, darkEdge);
  grad.addColorStop(0.35, color);
  grad.addColorStop(0.6, brightCore);
  grad.addColorStop(1, color);
  ctx.fillStyle = grad;
  roundRectPath(ctx, x + 1.5, y + 1.5, w - 3, h - 3, r - 1);
  ctx.fill();
  ctx.restore();

  // 3. Dark glass rim.
  ctx.save();
  ctx.strokeStyle = darken(color, 0.7);
  ctx.lineWidth = 1.3;
  roundRectPath(ctx, x + 0.75, y + 0.75, w - 1.5, h - 1.5, r);
  ctx.stroke();
  ctx.restore();

  // 4. Specular highlight — thin bright stripe near the left edge.
  ctx.save();
  ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
  roundRectPath(ctx, x + 2.5, y + 6, 1.8, h - 12, 1);
  ctx.fill();
  // Secondary thinner highlight on the right edge.
  ctx.fillStyle = "rgba(255, 255, 255, 0.22)";
  roundRectPath(ctx, x + w - 3.5, y + 10, 1.2, h - 20, 0.6);
  ctx.fill();
  ctx.restore();

  // 5. Gold corked bands at top + bottom (as though the vial is capped).
  ctx.save();
  ctx.fillStyle = "#E5C870";
  roundRectPath(ctx, x, y, w, 4, r);
  ctx.fill();
  roundRectPath(ctx, x, y + h - 4, w, 4, r);
  ctx.fill();
  // Tiny darker cork rim
  ctx.fillStyle = "rgba(94, 61, 20, 0.6)";
  ctx.fillRect(x, y + 4, w, 1);
  ctx.fillRect(x, y + h - 5, w, 1);
  ctx.restore();

  if (aegis) {
    ctx.save();
    ctx.strokeStyle = "#F2F6FF";
    ctx.lineWidth = 2;
    roundRectPath(ctx, x - 1, y - 1, w + 2, h + 2, r + 1);
    ctx.stroke();
    ctx.restore();
  }
}

function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

// Ball as a small lacquered wooden disc — no glow. Dark walnut body with
// a darker varnish rim and a bright specular highlight in the upper-left
// quadrant to read as polished lacquer against the bright gold table.
export function drawBall(ctx, x, y, r, isScoring = true) {
  const main = isScoring ? "#3E1F08" : "#5A2E10"; // walnut for main, mahogany for extras
  const rim = "#1A0A02";
  const shine = "rgba(255, 235, 200, 0.85)";

  // Varnish rim (slightly larger circle behind the body)
  ctx.fillStyle = rim;
  ctx.beginPath();
  ctx.arc(x, y, r + 1, 0, Math.PI * 2);
  ctx.fill();

  // Wood body
  ctx.fillStyle = main;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();

  // Faint grain — single short stroke for woodgrain texture
  ctx.save();
  ctx.strokeStyle = "rgba(255, 220, 170, 0.18)";
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(x - r * 0.6, y + r * 0.15);
  ctx.quadraticCurveTo(x, y + r * 0.05, x + r * 0.6, y + r * 0.2);
  ctx.stroke();
  ctx.restore();

  // Lacquer specular highlight (upper-left, slightly tilted)
  ctx.save();
  ctx.fillStyle = shine;
  ctx.beginPath();
  ctx.ellipse(x - r * 0.35, y - r * 0.38, r * 0.36, r * 0.22, -0.6, 0, Math.PI * 2);
  ctx.fill();
  // Tiny secondary pin-light
  ctx.fillStyle = "rgba(255, 255, 240, 0.7)";
  ctx.beginPath();
  ctx.arc(x - r * 0.45, y - r * 0.45, r * 0.1, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
